import { useState, useCallback, type FC, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import NumberBall from '@/components/base/NumberBall';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { validateOrFallback, validateLottoCombination } from '@/lib/lottoCombination';
import { generateLucky } from '@/lib/numberGenerators';
import QRShare from '@/components/feature/QRShare';
import Reveal from '@/components/base/Reveal';
import ResponsibleNotice from '@/components/feature/ResponsibleNotice';

const LuckyGenerator: FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [numbers, setNumbers] = useState<number[]>([]);
  const [bonus, setBonus] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [saveError, setSaveError] = useState('');
  const [qrModal, setQrModal] = useState(false);

  const generateLuckyNumbers = useCallback((e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSaved(false);
    setSaveLabel('');

    if (!name.trim()) {
      setError('이름을 입력해주세요');
      return;
    }
    if (!birthDate) {
      setError('생년월일을 입력해주세요');
      return;
    }

    const generated = generateLucky(name.trim(), birthDate);
    const validated = validateOrFallback(generated.numbers, generated.bonus);
    setNumbers(validated.numbers);
    setBonus(validated.bonus);
    setShowResult(true);
  }, [name, birthDate]);

  const handleSave = async () => {
    if (saved || numbers.length !== 6 || bonus === null) return;

    if (!user) {
      navigate('/login');
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      // 저장 직전에는 fail-closed: validator 통과 시에만 DB 저장한다.
      // 유효하지 않으면 화면 번호를 다른 랜덤 번호로 교체하지 않고 insert 도 하지 않는다.
      const validation = validateLottoCombination(numbers, bonus);
      if (!validation.valid) {
        setSaveError('생성된 번호가 유효하지 않습니다. 번호를 다시 생성해주세요.');
        return;
      }
      const validated = validation.combination; // 검증 통과 시 반환된 정렬된 조합 저장
      // 주 데이터 저장: 실제 생성 행을 확인해 실패를 성공으로 표시하지 않는다
      const { data: inserted, error: insertError } = await supabase
        .from('saved_numbers')
        .insert({
          user_id: user.id,
          numbers: validated.numbers,
          bonus: validated.bonus,
          generator_type: 'lucky',
          label: saveLabel.trim() || null,
        })
        .select('id')
        .single();

      if (insertError || !inserted) {
        if (import.meta.env.DEV) {
          console.error('[LuckyGenerator] 번호 저장 실패:', insertError?.message ?? 'no row');
        }
        setSaveError('번호를 저장하지 못했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      // 주 데이터 저장 성공 → 성공 UI 표시
      setSaved(true);

      // 보조 활동 로그: 이름·번호·bonus 등 개인정보를 기록하지 않음
      try {
        const { error: logError } = await supabase.from('user_activity_logs').insert({
          user_id: user.id,
          action: 'generate_lucky',
          detail: {},
        });
        if (logError && import.meta.env.DEV) {
          console.error('[LuckyGenerator] 활동 로그 기록 실패:', logError.message);
        }
      } catch (logErr) {
        if (import.meta.env.DEV) {
          console.error('[LuckyGenerator] 활동 로그 기록 예외:', logErr);
        }
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[LuckyGenerator] 번호 저장 예외:', err);
      }
      setSaveError('번호를 저장하지 못했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="w-full">
      <Reveal className="w-full max-w-3xl mx-auto px-4 md:px-6 py-14 md:py-20 border-t border-background-200">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-3">
            행운의 번호 생성기
          </h2>
          <p className="text-foreground-500 text-base md:text-lg">
            내 이름과 생년월일로 만드는 특별한 행운 번호
          </p>
        </div>

        <div className="bg-background-100 rounded-2xl p-8 md:p-12 border border-background-200">
          <form onSubmit={generateLuckyNumbers} className="max-w-md mx-auto space-y-5">
            <div>
              <label htmlFor="lucky-name" className="block text-sm font-medium text-foreground-600 mb-2">
                이름
              </label>
              <input
                id="lucky-name"
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setError(''); }}
                placeholder="홍길동"
                maxLength={20}
                className="
                  w-full px-4 py-3 rounded-xl
                  bg-background-50 border border-background-200
                  text-foreground-800 text-sm
                  placeholder:text-foreground-300
                  focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent
                  transition-all duration-200
                "
              />
            </div>

            <div>
              <label htmlFor="lucky-birth" className="block text-sm font-medium text-foreground-600 mb-2">
                생년월일
              </label>
              <input
                id="lucky-birth"
                type="date"
                value={birthDate}
                onChange={(e) => { setBirthDate(e.target.value); setError(''); }}
                className="
                  w-full px-4 py-3 rounded-xl
                  bg-background-50 border border-background-200
                  text-foreground-800 text-sm
                  focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent
                  transition-all duration-200
                "
              />
            </div>

            {error && (
              <p className="text-accent-700 text-sm text-center">{error}</p>
            )}

            <button
              type="submit"
              className="
                w-full py-3.5 rounded-full
                bg-accent-500 hover:bg-accent-600
                text-accent-950 font-bold text-base
                transition-all duration-200
                whitespace-nowrap cursor-pointer
              "
            >
              <i className="ri-sparkling-2-line mr-2"></i>
              행운 번호 생성하기
            </button>
          </form>

          {showResult && numbers.length === 6 && bonus !== null && (
            <div className="mt-10 pt-8 border-t border-background-200">
              <p className="text-center text-foreground-500 text-sm mb-5">
                <strong className="text-foreground-800">{name || '사용자'}</strong>님의 행운 번호
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {numbers.map((num, idx) => (
                  <NumberBall
                    key={num}
                    number={num}
                    size="lg"
                    animated
                    animationDelay={idx * 100}
                  />
                ))}
                <div className="flex items-center justify-center w-8 h-14">
                  <span className="text-foreground-400 text-xl font-bold">+</span>
                </div>
                <NumberBall
                  number={bonus}
                  isBonus
                  size="lg"
                  animated
                  animationDelay={600}
                />
              </div>

              {/* Save area */}
              <div className="flex flex-col items-center gap-3 mt-5 pt-5 border-t border-background-200 max-w-sm mx-auto">
                {saved ? (
                  <div className="flex items-center gap-2 text-secondary-600">
                    <i className="ri-check-double-line"></i>
                    <span className="text-sm font-medium">저장 완료!</span>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={saveLabel}
                      onChange={(e) => setSaveLabel(e.target.value)}
                      placeholder="라벨 (선택사항)"
                      className="w-full px-4 py-2 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-secondary-600 hover:bg-secondary-700 text-white font-medium text-sm transition-all duration-200 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-bookmark-line"></i>
                      {saving ? '저장 중...' : '번호 저장하기'}
                    </button>
                    {saveError && (
                      <p className="text-xs text-accent-600">{saveError}</p>
                    )}
                    {!user && (
                      <p className="text-xs text-foreground-400">
                        저장하려면 <button onClick={() => navigate('/login')} className="text-primary-600 underline cursor-pointer">로그인</button>이 필요합니다.
                      </p>
                    )}
                  </>
                )}
                <button
                  onClick={() => setQrModal(true)}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-background-300 text-foreground-600 hover:bg-background-100 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-qr-code-line"></i>
                  QR 공유
                </button>
              </div>

              {qrModal && (
                <QRShare numbers={numbers} bonus={bonus} label={name || undefined} onClose={() => setQrModal(false)} />
              )}
            </div>
          )}
        </div>
      </Reveal>

      <ResponsibleNotice />
    </section>
  );
};

export default LuckyGenerator;